import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, Save } from 'lucide-react'
import type { ClassSignal, SignalConflictItem, SignalKind } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Textarea'
import { Chips } from '@/components/ui/Chips'
import { classesApi, classesQueryKey } from '../api'
import { extractApiError } from '@/lib/api'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

interface Props {
  classId: string
  signals: ClassSignal[]
}

interface FormState {
  excerpts: string[]
  excerptDraft: string
  keywordsPos: string[]
  keywordsNeg: string[]
  legalArticles: string[]
}

function fromSignals(signals: ClassSignal[]): FormState {
  return {
    excerpts: signals.filter((s) => s.kind === 'EXCERPT').map((s) => s.content),
    excerptDraft: '',
    keywordsPos: signals.filter((s) => s.kind === 'KEYWORD_POS').map((s) => s.content),
    keywordsNeg: signals.filter((s) => s.kind === 'KEYWORD_NEG').map((s) => s.content),
    legalArticles: signals.filter((s) => s.kind === 'LEGAL_ARTICLE').map((s) => s.content),
  }
}

/** Mirror of backend normalizeForCompare. */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const KIND_LABEL_PT: Record<SignalKind, string> = {
  EXCERPT: 'trecho',
  KEYWORD_POS: 'positiva',
  KEYWORD_NEG: 'negativa',
  LEGAL_ARTICLE: 'artigo',
}

function describeConflict(item: SignalConflictItem): string {
  return item.conflictsWith.map((c) => `${c.className} (${KIND_LABEL_PT[c.signalKind]})`).join(', ')
}

export function SignalsEditor({ classId, signals }: Props) {
  const qc = useQueryClient()
  const [state, setState] = useState<FormState>(() => fromSignals(signals))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setState(fromSignals(signals))
  }, [signals])

  /* ---- Conflict detection (debounced) ---- */
  const debouncedKeywordsPos = useDebouncedValue(state.keywordsPos, 400)
  const debouncedKeywordsNeg = useDebouncedValue(state.keywordsNeg, 400)
  const debouncedLegalArticles = useDebouncedValue(state.legalArticles, 400)

  const hasAnyChip =
    debouncedKeywordsPos.length + debouncedKeywordsNeg.length + debouncedLegalArticles.length > 0

  const conflictsQuery = useQuery({
    queryKey: [
      ...classesQueryKey.detail(classId),
      'conflicts',
      debouncedKeywordsPos,
      debouncedKeywordsNeg,
      debouncedLegalArticles,
    ],
    queryFn: () =>
      classesApi.checkSignalConflicts(classId, {
        excerpts: [],
        keywordsPos: debouncedKeywordsPos,
        keywordsNeg: debouncedKeywordsNeg,
        legalArticles: debouncedLegalArticles,
      }),
    enabled: hasAnyChip,
    staleTime: 30_000,
  })

  const conflicts = conflictsQuery.data?.conflicts ?? []

  const conflictByKey = useMemo(() => {
    const m = new Map<string, SignalConflictItem>()
    for (const c of conflicts) m.set(`${c.kind}:${normalize(c.content)}`, c)
    return m
  }, [conflicts])

  function getWarning(kind: SignalKind, value: string): string | null {
    const c = conflictByKey.get(`${kind}:${normalize(value)}`)
    if (!c) return null
    return `Também usado em: ${describeConflict(c)}`
  }

  /* ---- Save ---- */
  const mutation = useMutation({
    mutationFn: () =>
      classesApi.replaceSignals(classId, {
        excerpts: [...state.excerpts, ...(state.excerptDraft.trim() ? [state.excerptDraft.trim()] : [])],
        keywordsPos: state.keywordsPos,
        keywordsNeg: state.keywordsNeg,
        legalArticles: state.legalArticles,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) })
      setState((s) => ({ ...s, excerptDraft: '' }))
    },
    onError: (err) => setError(extractApiError(err).message),
  })

  function addExcerpt() {
    const raw = state.excerptDraft
    if (!raw.trim()) return
    const pieces = raw
      .split(/[;\n\r]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    if (pieces.length === 0) return
    setState((s) => {
      const seen = new Set(s.excerpts)
      const next = [...s.excerpts]
      for (const p of pieces) {
        if (!seen.has(p)) {
          next.push(p)
          seen.add(p)
        }
      }
      return { ...s, excerpts: next, excerptDraft: '' }
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <Field
        label="Trechos típicos"
        hint='Frases ou parágrafos típicos. Cole vários separados por ";" ou em linhas diferentes — viram cards separados ao adicionar. Vírgula NÃO separa (texto jurídico tem vírgula).'
      >
        <div className="flex flex-col gap-2">
          {state.excerpts.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {state.excerpts.map((ex, i) => (
                <li
                  key={i}
                  className="group flex items-start gap-2 px-3 py-2 rounded-md border border-border-subtle bg-bg-subtle text-sm text-fg-2"
                >
                  <span className="data-mono text-2xs text-fg-3 mt-0.5">[{i + 1}]</span>
                  <span className="flex-1 line-clamp-3">{ex}</span>
                  <button
                    type="button"
                    onClick={() => setState((s) => ({ ...s, excerpts: s.excerpts.filter((_, n) => n !== i) }))}
                    className="text-fg-3 hover:text-danger text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Remover
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 items-start">
            <Textarea
              rows={3}
              placeholder={
                'Cole 1 trecho aqui ou vários colados separados por ";".\nEx: Designa-se audiência de instrução; Fica designada a sessão; Marca-se data para...'
              }
              value={state.excerptDraft}
              onChange={(e) => setState((s) => ({ ...s, excerptDraft: e.target.value }))}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault()
                  addExcerpt()
                }
              }}
            />
            <Button variant="secondary" size="sm" onClick={addExcerpt} className="shrink-0 mt-1">
              Adicionar
            </Button>
          </div>
        </div>
      </Field>

      <Field
        label="Palavras-chave positivas"
        hint="Palavras que SUPORTAM essa classificação. Enter ou vírgula pra adicionar — colar texto com vírgulas/quebras já vira chips."
      >
        <Chips
          tone="success"
          values={state.keywordsPos}
          onChange={(v) => setState((s) => ({ ...s, keywordsPos: v }))}
          placeholder="ex: audiência, designada, instrução…"
          getWarning={(v) => getWarning('KEYWORD_POS', v)}
        />
      </Field>

      <Field
        label="Palavras-chave negativas"
        hint="Quando aparecem, indicam que NÃO é essa classe (vira filtro de exclusão). Colar texto com vírgulas/quebras já vira chips."
      >
        <Chips
          tone="danger"
          values={state.keywordsNeg}
          onChange={(v) => setState((s) => ({ ...s, keywordsNeg: v }))}
          placeholder="ex: cancelada, redesignada, conciliação…"
          getWarning={(v) => getWarning('KEYWORD_NEG', v)}
        />
      </Field>

      <Field
        label="Artigos jurídicos relevantes"
        hint="Citações que costumam aparecer junto. Colar texto com vírgulas/quebras já vira chips."
      >
        <Chips
          tone="brand"
          values={state.legalArticles}
          onChange={(v) => setState((s) => ({ ...s, legalArticles: v }))}
          placeholder="ex: Art. 357 CPC, Art. 360 CPC…"
          getWarning={(v) => getWarning('LEGAL_ARTICLE', v)}
        />
      </Field>

      {conflicts.length > 0 && (
        <div className="rounded-md border border-warning/30 bg-warning-bg/40 px-4 py-3">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-warning">
                {conflicts.length} termo{conflicts.length > 1 ? 's' : ''} também usado{conflicts.length > 1 ? 's' : ''} em outras classes
              </h4>
              <p className="text-xs text-fg-2 mt-0.5">
                Não é erro — é só pra você decidir: manter com peso menor depois (ao gerar a regex), remover daqui, ou remover da outra classe.
              </p>
            </div>
          </div>
          <ul className="mt-2 flex flex-col gap-1.5 text-xs">
            {conflicts.map((c, i) => (
              <li key={i} className="flex items-start gap-2 pl-6">
                <span className="data-mono bg-warning/10 px-1.5 py-0.5 rounded text-warning shrink-0">
                  {KIND_LABEL_PT[c.kind]}
                </span>
                <span className="font-medium text-fg-1">"{c.content}"</span>
                <span className="text-fg-3">→</span>
                <span className="text-fg-2">
                  {c.conflictsWith.map((m, j) => (
                    <span key={j}>
                      {j > 0 && ', '}
                      <Link to={`/classes/${m.classId}`} className="underline hover:text-warning">
                        {m.className}
                      </Link>{' '}
                      <span className="text-fg-3">({KIND_LABEL_PT[m.signalKind]})</span>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">{error}</div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
          <Save className="h-4 w-4" />
          Salvar sinais
        </Button>
      </div>
    </div>
  )
}
